/**
 * Message-variable tokens for automation titles/bodies — the editor-side
 * mirror of website/automations/variables.py.
 *
 * Token syntax: `{{key}}` or `{{key - FORMAT}}` (canonical; `{{key|FORMAT}}`
 * is parsed too). The backend is the source of truth for which variables and
 * formats exist — the editor reads them from the conditions metadata
 * (`meta.variables`) and only mirrors the validation so Save can be disabled
 * and the live preview can explain what is wrong.
 */

// {{ key }} / {{ key - FORMAT }} / {{ key | FORMAT }}
const TOKEN_SOURCE = "\\{\\{\\s*([A-Za-z][A-Za-z0-9_]*)\\s*(?:(?:\\||-)\\s*([^}]*?))?\\s*\\}\\}";

const newTokenRegex = () => new RegExp(TOKEN_SOURCE, "g");

/** Every token in `text`, in order: [{ key, format, raw, start, end }]. */
export const parseTemplateTokens = (text) => {
  const tokens = [];
  const re = newTokenRegex();
  const source = String(text || "");
  let match = re.exec(source);
  while (match) {
    const format = (match[2] || "").trim() || null;
    tokens.push({ key: match[1], format, raw: match[0], start: match.index, end: match.index + match[0].length });
    match = re.exec(source);
  }
  return tokens;
};

export const templateHasVariables = (text) => parseTemplateTokens(text).length > 0;

/** Canonical token text: `{{key}}` or `{{key - FORMAT}}`. */
export const canonicalToken = (key, format) => (format ? `{{${key} - ${format}}}` : `{{${key}}}`);

/** Human label: "paid_plan_expiration_date - YYYY-MM-DD" / "username". */
export const variableLabel = (key, format) => (format ? `${key} - ${format}` : key);

export const findVariable = (variables, key) => (variables || []).find((v) => v.key === key) || null;

export const defaultFormatFor = (variable) =>
  variable?.default_format || (Array.isArray(variable?.formats) && variable.formats[0]?.key) || null;

export const variableAllowsFormat = (variable) =>
  Boolean(variable?.allows_format) || (Array.isArray(variable?.formats) && variable.formats.length > 0);

/**
 * Problems with the tokens in `text` against the known variables:
 * [{ token, message }]. Empty when `variables` is not loaded yet (the backend
 * still validates on save).
 */
export const validateTemplateTokens = (text, variables) => {
  if (!Array.isArray(variables) || variables.length === 0) return [];
  const problems = [];
  parseTemplateTokens(text).forEach((token) => {
    const variable = findVariable(variables, token.key);
    if (!variable) {
      problems.push({ token, message: `Unknown variable "${token.key}".` });
      return;
    }
    if (token.format) {
      if (!variableAllowsFormat(variable)) {
        problems.push({ token, message: `"${token.key}" does not take a format.` });
      } else if (!(variable.formats || []).some((f) => f.key === token.format)) {
        problems.push({ token, message: `Unknown format "${token.format}" for "${token.key}".` });
      }
    }
  });
  return problems;
};

/** Case-insensitive filter on key / label / description. */
export const filterVariables = (variables, query) => {
  const q = String(query || "").trim().toLowerCase();
  const list = Array.isArray(variables) ? variables : [];
  if (!q) return list;
  return list.filter((v) => [v.key, v.label, v.description].some((s) => String(s || "").toLowerCase().includes(q)));
};

/**
 * The "@query" being typed right before the caret, or null. Only an `@` at
 * the start of the text or after whitespace / `(` / `[` counts, so e-mail
 * addresses ("mail@example") don't open the menu.
 * Returns { start, query } where `start` is the index of the `@`.
 */
export const findMentionQuery = (text, caret) => {
  const before = String(text || "").slice(0, Math.max(0, Number(caret) || 0));
  const match = /(^|[\s([])@([A-Za-z0-9_]*)$/.exec(before);
  if (!match) return null;
  const start = before.length - match[2].length - 1;
  return { start, query: match[2] };
};

/**
 * Replace [start, end) of `text` with `token`, adding one space after it when
 * the next character is not already whitespace (so typing continues
 * naturally). Returns { text, caret } with the caret placed after the token.
 */
export const insertTokenAtRange = (text, start, end, token) => {
  const source = String(text || "");
  const before = source.slice(0, start);
  const after = source.slice(end);
  const needsSpace = after.length === 0 || !/^\s/.test(after);
  const inserted = token + (needsSpace ? " " : "");
  return { text: before + inserted + after, caret: before.length + inserted.length };
};

/** Rewrite one token's format in place (null/"" → default token without format). */
export const replaceTokenFormat = (text, token, newFormat) => {
  const source = String(text || "");
  return source.slice(0, token.start) + canonicalToken(token.key, newFormat || null) + source.slice(token.end);
};

/** Delete one token; collapses the doubled space it leaves behind. */
export const removeToken = (text, token) => {
  const source = String(text || "");
  let before = source.slice(0, token.start);
  let after = source.slice(token.end);
  if (/\s$/.test(before) && /^\s/.test(after)) after = after.replace(/^\s/, "");
  else if (before.length === 0 && /^\s/.test(after)) after = after.replace(/^\s/, "");
  return before + after;
};

/** Normalise every token in `text` to the canonical form (what the backend stores). */
export const canonicalizeTemplate = (text) =>
  String(text || "").replace(newTokenRegex(), (_m, key, format) => canonicalToken(key, (format || "").trim() || null));
